import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Tabs,
  Tab,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
  Divider,
  CircularProgress,
  LinearProgress,
  IconButton,
  Chip,
} from "@mui/material";
import { Receipt, Download, Close, Payments as PaymentsIcon, AccountBalance, UploadFile, InsertDriveFile } from "@mui/icons-material";
import { useSnackbar } from "notistack";
import dayjs from "dayjs";

import invoiceService from "../../services/invoiceService";
import systemSettingsService from "../../services/systemSettingsService";
import StatusChip from "../../components/common/StatusChip";
import PageHeader from "../../components/common/PageHeader";
import { selectKeyForRole } from "../../redux/slices/statusDefinitionsSlice";
import { selectActiveTin } from "../../redux/slices/authSlice";
import EmptyState from "../../components/common/EmptyState";
import KpiCard from "../../components/common/KpiCard";
import SkeletonLoader from "../../components/common/SkeletonLoader";

// Only these two — Waypoint doesn't take card payments online, so "Pay Now"
// here just records intent/method, not an actual online charge. Both are
// reconciled manually by an admin once the transfer/cash is confirmed.
const PAYMENT_METHODS = [
  { value: "bank_transfer", label: "Bank Transfer" },
  { value: "cash", label: "Cash by Hand" },
];

const PaymentDialog = ({ invoice, open, onClose, onSuccess }) => {
  const { enqueueSnackbar } = useSnackbar();
  const [loading, setLoading] = useState(false);
  const [method, setMethod] = useState("bank_transfer");
  const [bankDetails, setBankDetails] = useState(null);
  const [bankLoading, setBankLoading] = useState(false);
  const [proofFile, setProofFile] = useState(null);

  useEffect(() => {
    if (!open || method !== "bank_transfer" || bankDetails) return;
    setBankLoading(true);
    systemSettingsService
      .getBankDetails()
      .then(({ data }) => setBankDetails(data.data.bankDetails))
      .catch(() => enqueueSnackbar("Failed to load bank details", { variant: "error" }))
      .finally(() => setBankLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, method]);

  useEffect(() => {
    if (!open) setProofFile(null);
  }, [open]);

  // Mark as Paid is now a REQUEST, not an immediate payment — the customer
  // uploads proof (a screenshot/photo of the transfer confirmation, say),
  // and the invoice sits pending review until an admin actually confirms
  // the money arrived (invoiceController.approvePendingPayment). Nothing
  // financial changes on the invoice until that approval happens.
  const handleSubmitPayment = async () => {
    if (!proofFile) {
      enqueueSnackbar("Please attach proof of payment (a screenshot or photo).", { variant: "warning" });
      return;
    }
    setLoading(true);
    try {
      const { data: presigned } = await invoiceService.getPaymentProofUploadUrl(invoice._id, proofFile);
      const { key, url, originalName } = presigned.data;
      await fetch(url, { method: "PUT", body: proofFile, headers: { "Content-Type": proofFile.type } });

      await invoiceService.submitPayment(invoice._id, {
        amount: invoice.balanceDue,
        method,
        proofS3Key: key,
        proofOriginalName: originalName,
      });
      enqueueSnackbar(
        "Thanks! Your payment proof was submitted — we'll confirm it shortly.",
        { variant: "success" },
      );
      onSuccess();
      onClose();
    } catch (err) {
      enqueueSnackbar(
        err.response?.data?.message || "Something went wrong. Please try again.",
        { variant: "error" },
      );
    } finally {
      setLoading(false);
    }
  };

  const handleDownloadPdf = () => {
    // The print route/API both key off the invoice's Mongo _id, not its
    // human-readable invoiceNumber (CustomerInvoicePrint -> invoiceService.
    // getPdf(id) -> GET /invoices/:id/pdf -> Invoice.findById) — this was
    // passing invoiceNumber, which 404s/errors against that route.
    window.open(`/dashboard/invoices/${invoice._id}/print`, "_blank");
  };

  if (!invoice) return null;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{ sx: { borderRadius: 3 } }}
    >
      <DialogTitle
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <Typography fontWeight={700}>Pay Invoice</Typography>
        <IconButton size="small" onClick={onClose}>
          <Close fontSize="small" />
        </IconButton>
      </DialogTitle>
      <DialogContent>
        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", bgcolor: "action.hover", borderRadius: 2, p: 2, mb: 2.5 }}>
          <Box>
            <Typography variant="caption" color="text.secondary" display="block">
              {invoice.invoiceNumber}
            </Typography>
            <Typography fontSize={13} fontWeight={600} mt={0.5}>
              {invoice.shipment?.packageDescription || "Shipment charges"}
            </Typography>
            <Typography variant="caption" color="text.secondary" display="block" mt={0.5}>
              Due {invoice.dueDate ? dayjs(invoice.dueDate).format("MMM D, YYYY") : "—"}
            </Typography>
          </Box>
          <Button size="small" startIcon={<Download fontSize="small" />} onClick={handleDownloadPdf}>
            PDF
          </Button>
        </Box>

        {/* Itemized packages, if this invoice was generated from specific items */}
        {(invoice.items || []).length > 0 && (
          <>
            <Typography variant="overline" color="text.secondary" display="block" mb={0.5}>
              Items
            </Typography>
            {invoice.items.map((it, i) => (
              <Box key={i} sx={{ display: "flex", justifyContent: "space-between", py: 0.5 }}>
                <Box>
                  <Typography variant="body2">{it.description}</Typography>
                  <Typography variant="caption" color="text.secondary" sx={{ fontFamily: "monospace" }}>
                    {it.trackingNumber}
                  </Typography>
                </Box>
                <Typography variant="body2" color="text.secondary">
                  CI${(it.totalValue || 0).toFixed(2)}
                </Typography>
              </Box>
            ))}
            <Divider sx={{ my: 1.5 }} />
          </>
        )}

        <Typography variant="overline" color="text.secondary" display="block" mb={0.5}>
          Charges
        </Typography>
        {(invoice.lines || []).map((line, i) => (
          <Box
            key={i}
            sx={{ display: "flex", justifyContent: "space-between", py: 0.75 }}
          >
            <Typography variant="body2" color="text.secondary">
              {line.description}
            </Typography>
            <Typography variant="body2" fontWeight={600}>
              CI${(line.unitPrice * (line.quantity || 1)).toFixed(2)}
            </Typography>
          </Box>
        ))}

        <Box sx={{ mt: 1 }}>
          <Box sx={{ display: "flex", justifyContent: "space-between", py: 0.5 }}>
            <Typography variant="body2" color="text.secondary">Subtotal</Typography>
            <Typography variant="body2">CI${(invoice.subtotal || 0).toFixed(2)}</Typography>
          </Box>
          {invoice.taxAmount > 0 && (
            <Box sx={{ display: "flex", justifyContent: "space-between", py: 0.5 }}>
              <Typography variant="body2" color="text.secondary">Tax</Typography>
              <Typography variant="body2">CI${invoice.taxAmount.toFixed(2)}</Typography>
            </Box>
          )}
          {invoice.dutyAmount > 0 && (
            <Box sx={{ display: "flex", justifyContent: "space-between", py: 0.5 }}>
              <Typography variant="body2" color="text.secondary">Duty</Typography>
              <Typography variant="body2">CI${invoice.dutyAmount.toFixed(2)}</Typography>
            </Box>
          )}
          {invoice.amountPaid > 0 && (
            <Box sx={{ display: "flex", justifyContent: "space-between", py: 0.5 }}>
              <Typography variant="body2" color="text.secondary">Amount Paid</Typography>
              <Typography variant="body2" color="success.main">
                −CI${invoice.amountPaid.toFixed(2)}
              </Typography>
            </Box>
          )}
        </Box>

        <Divider sx={{ my: 1.5 }} />
        <Box sx={{ display: "flex", justifyContent: "space-between", mb: 2.5 }}>
          <Typography fontWeight={700}>Total Due (KYD)</Typography>
          <Typography fontWeight={700} fontSize={18} color="primary.main">
            CI${(invoice.balanceDue || 0).toFixed(2)}
          </Typography>
        </Box>

        <TextField
          select
          fullWidth
          label="Payment Method"
          value={method}
          onChange={(e) => setMethod(e.target.value)}
          size="small"
          sx={{ mb: method === "bank_transfer" ? 2 : 0 }}
        >
          {PAYMENT_METHODS.map((m) => (
            <MenuItem key={m.value} value={m.value}>
              {m.label}
            </MenuItem>
          ))}
        </TextField>

        {method === "bank_transfer" && (
          // Translucent primary.main tint instead of a solid light-blue hex
          // — the text above is theme-following (default/"text.secondary"),
          // which went light-on-light-blue in dark mode against a fixed bg.
          <Box sx={{ bgcolor: "rgba(13,98,155,0.08)", borderRadius: 2, p: 2 }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}>
              <AccountBalance fontSize="small" color="primary" />
              <Typography variant="body2" fontWeight={700}>
                Bank Transfer Details
              </Typography>
            </Box>
            {bankLoading ? (
              <CircularProgress size={18} />
            ) : bankDetails ? (
              <>
                <Typography variant="body2" fontWeight={600}>
                  {bankDetails.accountHolder}
                </Typography>
                {(bankDetails.accounts || []).map((acc, i) => (
                  <Typography key={i} variant="body2" color="text.secondary">
                    {acc.number} — {acc.label}
                  </Typography>
                ))}
                <Typography variant="body2" color="text.secondary">
                  {bankDetails.bankName}
                </Typography>
                <Typography variant="caption" color="text.secondary" display="block" mt={1}>
                  Please reference invoice {invoice.invoiceNumber} on your transfer.
                </Typography>
              </>
            ) : (
              <Typography variant="body2" color="text.secondary">
                Bank details unavailable right now — contact us for transfer instructions.
              </Typography>
            )}
          </Box>
        )}

        <Box sx={{ mt: 2 }}>
          <Typography variant="body2" fontWeight={600} sx={{ mb: 0.75 }}>
            Proof of Payment
          </Typography>
          <Button
            component="label"
            variant="outlined"
            fullWidth
            startIcon={<UploadFile />}
            sx={{ justifyContent: "flex-start", textTransform: "none" }}
          >
            {proofFile ? proofFile.name : "Attach a screenshot or photo of your transfer"}
            <input
              type="file"
              hidden
              accept="image/jpeg,image/png,image/webp,application/pdf"
              onChange={(e) => setProofFile(e.target.files?.[0] || null)}
            />
          </Button>
          {proofFile && (
            <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, mt: 0.75 }}>
              <InsertDriveFile fontSize="small" color="action" />
              <Typography variant="caption" color="text.secondary">
                {(proofFile.size / 1024).toFixed(0)} KB
              </Typography>
            </Box>
          )}
          <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.75 }}>
            An admin will review this and confirm once the payment is received — the invoice stays pending until
            then.
          </Typography>
        </Box>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2.5 }}>
        <Button onClick={onClose} variant="outlined">
          Cancel
        </Button>
        <Button
          onClick={handleSubmitPayment}
          variant="contained"
          disabled={loading || !proofFile}
          startIcon={loading ? null : method === "bank_transfer" ? <AccountBalance /> : <PaymentsIcon />}
        >
          {loading ? (
            <CircularProgress size={18} color="inherit" />
          ) : (
            `Submit CI$${(invoice.balanceDue || 0).toFixed(2)} for Review`
          )}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

const CustomerPayments = () => {
  const navigate = useNavigate();
  const paidKey = useSelector(selectKeyForRole("invoice", "paid"));
  const activeTin = useSelector(selectActiveTin);
  const [tab, setTab] = useState(0);
  const [invoices, setInvoices] = useState([]);
  const [payments, setPayments] = useState([]);
  const [loading, setLoading] = useState(false);
  const [payDialog, setPayDialog] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const [invRes, payRes] = await Promise.all([
        invoiceService.getAll({ tin: activeTin || undefined }),
        invoiceService.getPayments(),
      ]);
      setInvoices(invRes.data.data || []);
      setPayments(payRes.data.data?.payments || []);
    } catch {
      setInvoices([]);
      setPayments([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTin]);

  const outstanding = invoices.filter((i) => i.status && i.status !== paidKey);
  const totalOwed = outstanding.reduce((s, i) => s + (i.balanceDue || 0), 0);
  const totalPaid = invoices
    .filter((i) => i.status === paidKey)
    .reduce((s, i) => s + (i.totalAmount || 0), 0);

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
      <PageHeader
        title="Payments & Invoices"
        subtitle="View your invoices and payment history"
      />

      {/* KPIs */}
      <Box
        sx={{
          display: "grid", gridTemplateColumns: {
            xs: "1fr",
            sm: "repeat(3, 1fr)",
            lg: "repeat(3, 1fr)",
          }, gap: 2
        }}
      >
        <KpiCard
          label="Outstanding Balance"
          value={loading ? "—" : `CI$${totalOwed.toFixed(2)}`}
          delta={
            outstanding.length > 0
              ? `${outstanding.length} invoice(s) pending`
              : "All paid up"
          }
          deltaType={totalOwed > 0 ? "down" : "neutral"}
          icon={<Receipt />}
          iconBg="#FFF5F5"
          iconColor="#C53030"
        />
        <KpiCard
          label="Total Paid"
          value={loading ? "—" : `CI$${totalPaid.toFixed(2)}`}
          delta="All time"
          deltaType="up"
          icon={<PaymentsIcon />}
          iconBg="#ECFDF5"
          iconColor="#0E9F6E"
        />
        <KpiCard
          label="Total Invoices"
          value={loading ? "—" : invoices.length.toString()}
          delta="All time"
          deltaType="neutral"
          icon={<Receipt />}
          iconBg="#EBF4FF"
          iconColor="#2B6CB0"
        />
      </Box>

      <Card>
        <Tabs
          value={tab}
          onChange={(_, v) => setTab(v)}
          sx={{
            px: 2,
            borderBottom: "1px solid",
            borderColor: "divider",
            "& .MuiTab-root": {
              fontSize: 13,
              textTransform: "none",
              fontWeight: 600,
              minHeight: 44,
            },
          }}
        >
          <Tab label={`All Invoices (${invoices.length})`} />
          <Tab label={`Outstanding (${outstanding.length})`} />
          <Tab label={`Payment History (${payments.length})`} />
        </Tabs>

        {loading ? (
          <SkeletonLoader rows={6} />
        ) : null}

        {/* All invoices */}
        {tab === 0 &&
          (!loading && invoices.length === 0 ? (
            <EmptyState
              icon="🧾"
              title="No invoices yet"
              description="Invoices will appear here once your shipments are processed."
            />
          ) : (
            <Box sx={{ overflowX: "auto" }}>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Invoice #</TableCell>
                    <TableCell>Shipment</TableCell>
                    <TableCell align="right">Amount</TableCell>
                    <TableCell align="right">Balance Due</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell>Due Date</TableCell>
                    <TableCell />
                  </TableRow>
                </TableHead>
                <TableBody>
                  {invoices.map((inv) => (
                    <TableRow key={inv._id}>
                      <TableCell
                        sx={{
                          fontFamily: "monospace",
                          fontSize: 12,
                          color: "text.secondary",
                        }}
                      >
                        {inv.invoiceNumber}
                      </TableCell>
                      <TableCell>
                        <Typography
                          variant="body2"
                          fontWeight={600}
                          noWrap
                          maxWidth={200}
                        >
                          {inv.shipment?.packageDescription ||
                            "Shipment charges"}
                        </Typography>
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          sx={{ fontFamily: "monospace" }}
                        >
                          {inv.shipment?.trackingNumber}
                        </Typography>
                      </TableCell>
                      <TableCell align="right" sx={{ fontWeight: 600 }}>
                        CI${(inv.totalAmount || 0).toFixed(2)}
                      </TableCell>
                      <TableCell
                        align="right"
                        sx={{
                          fontWeight: 700,
                          color:
                            inv.balanceDue > 0 ? "error.main" : "success.main",
                        }}
                      >
                        CI${(inv.balanceDue || 0).toFixed(2)}
                      </TableCell>
                      <TableCell>
                        <StatusChip status={inv.status} domain="invoice" />
                      </TableCell>
                      <TableCell sx={{ fontSize: 12, color: "text.secondary" }}>
                        {inv.dueDate
                          ? dayjs(inv.dueDate).format("MMM D, YYYY")
                          : "—"}
                      </TableCell>
                      <TableCell>
                        {inv.pendingPayment?.submittedAt ? (
                          <Chip size="small" color="warning" label="Pending Review" sx={{ fontSize: 11 }} />
                        ) : inv.balanceDue > 0 && inv.status !== paidKey ? (
                          <Button
                            size="small"
                            variant="contained"
                            sx={{ fontSize: 11 }}
                            onClick={() => setPayDialog(inv)}
                          >
                            Pay Now
                          </Button>
                        ) : (
                          <Button
                            size="small"
                            variant="outlined"
                            sx={{ fontSize: 11 }}
                            startIcon={<Download sx={{ fontSize: 13 }} />}
                            onClick={() => navigate(`/dashboard/invoices/${inv._id}/print`)}
                          >
                            Receipt
                          </Button>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Box>
          ))}

        {/* Outstanding */}
        {tab === 1 &&
          (!loading && outstanding.length === 0 ? (
            <EmptyState
              icon="✅"
              title="All paid up!"
              description="You have no outstanding invoices."
            />
          ) : (
            outstanding.map((inv) => (
              <Box
                key={inv._id}
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 2,
                  px: 2.5,
                  py: 2,
                  borderBottom: "1px solid",
                  borderColor: "divider",
                  flexWrap: "wrap",
                }}
              >
                <Box
                  sx={{
                    width: 44,
                    height: 44,
                    borderRadius: 2,
                    bgcolor: "error.light",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    flexShrink: 0,
                  }}
                >
                  <Receipt sx={{ color: "error.main", fontSize: 22 }} />
                </Box>
                <Box sx={{ flex: 1, minWidth: 180 }}>
                  <Typography fontWeight={700} fontSize={13}>
                    {inv.invoiceNumber}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {inv.shipment?.packageDescription || "Shipment charges"} ·{" "}
                    Due{" "}
                    {inv.dueDate
                      ? dayjs(inv.dueDate).format("MMM D, YYYY")
                      : "—"}
                  </Typography>
                </Box>
                <Typography
                  fontWeight={700}
                  fontSize={17}
                  color="error.main"
                  sx={{ mr: 1 }}
                >
                  CI${(inv.balanceDue || 0).toFixed(2)}
                </Typography>
                {inv.pendingPayment?.submittedAt ? (
                  <Chip size="small" color="warning" label="Pending Review" />
                ) : (
                  <Button
                    variant="contained"
                    size="small"
                    onClick={() => setPayDialog(inv)}
                  >
                    Pay Now
                  </Button>
                )}
              </Box>
            ))
          ))}

        {/* Payment history */}
        {tab === 2 &&
          (!loading && payments.length === 0 ? (
            <EmptyState
              icon="💳"
              title="No payments yet"
              description="Your payment history will appear here."
            />
          ) : (
            <Box sx={{ overflowX: "auto" }}>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Transaction ID</TableCell>
                    <TableCell>Invoice</TableCell>
                    <TableCell align="right">Amount</TableCell>
                    <TableCell>Method</TableCell>
                    <TableCell>Date</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell />
                  </TableRow>
                </TableHead>
                <TableBody>
                  {payments.map((p) => (
                    <TableRow key={p._id}>
                      <TableCell
                        sx={{
                          fontFamily: "monospace",
                          fontSize: 11,
                          color: "text.secondary",
                        }}
                      >
                        {p.transactionId || "—"}
                      </TableCell>
                      <TableCell sx={{ fontFamily: "monospace", fontSize: 12 }}>
                        {p.invoice?.invoiceNumber || "—"}
                      </TableCell>
                      <TableCell
                        align="right"
                        sx={{ fontWeight: 700, color: "success.main" }}
                      >
                        CI${(p.amount || 0).toFixed(2)}
                      </TableCell>
                      <TableCell
                        sx={{ textTransform: "capitalize", fontSize: 13 }}
                      >
                        {(p.method || "").replace("_", " ")}
                      </TableCell>
                      <TableCell sx={{ fontSize: 12, color: "text.secondary" }}>
                        {dayjs(p.processedAt || p.createdAt).format(
                          "MMM D, YYYY h:mm A",
                        )}
                      </TableCell>
                      <TableCell>
                        <StatusChip status={p.status} domain="payment" />
                      </TableCell>
                      <TableCell>
                        {p.invoice?._id && (
                          <Button
                            size="small"
                            variant="outlined"
                            sx={{ fontSize: 11 }}
                            startIcon={<Download sx={{ fontSize: 13 }} />}
                            onClick={() => navigate(`/dashboard/invoices/${p.invoice._id}/print`)}
                          >
                            Receipt
                          </Button>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Box>
          ))}
      </Card>

      <PaymentDialog
        invoice={payDialog}
        open={!!payDialog}
        onClose={() => setPayDialog(null)}
        onSuccess={load}
      />
    </Box>
  );
};

export default CustomerPayments;
